/**
 * AWI Instant Search
 *
 * Full-screen search modal backed entirely by Shopify's native predictive
 * search API. Results are rendered server side by the
 * `sections/awi-predictive-search.liquid` section and injected into the modal.
 */
class AwiSearchModal extends HTMLElement {
  static STORAGE_KEY = 'awi:recent-searches';
  static MAX_RECENT = 12;
  static DEBOUNCE = 250;

  constructor() {
    super();

    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl || '/search/suggest';
    this.searchUrl = this.dataset.searchUrl || '/search';

    this.form = this.querySelector('[data-awi-search-form]');
    this.input = this.querySelector('.awi-search__input');
    this.clearButton = this.querySelector('[data-awi-search-clear]');
    this.idleState = this.querySelector('[data-awi-idle]');
    this.resultsState = this.querySelector('[data-awi-results]');
    this.recent = this.querySelector('[data-awi-recent]');
    this.recentList = this.querySelector('[data-awi-recent-list]');
    this.scroller = this.querySelector('.awi-search__scroll');

    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;
    this.searchTerm = '';
    this.openerElement = null;

    this.setupEventListeners();
    this.renderRecentSearches();
  }

  /* ---------------------------------------------------------------- events */

  setupEventListeners() {
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.clearButton.addEventListener('click', this.onClear.bind(this));

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.querySelector('[data-awi-recent-clear]')?.addEventListener('click', () => {
      this.writeRecentSearches([]);
      this.renderRecentSearches();
    });

    this.setupCarousel();
  }

  onClick(event) {
    const close = event.target.closest('[data-awi-search-close]');
    if (close) {
      event.preventDefault();
      this.close();
      return;
    }

    const term = event.target.closest('[data-awi-search-term]');
    if (term) {
      event.preventDefault();
      this.search(term.dataset.awiSearchTerm, { focus: true, syncInput: true });
      return;
    }

    const removeChip = event.target.closest('[data-awi-recent-remove]');
    if (removeChip) {
      event.preventDefault();
      this.removeRecentSearch(removeChip.dataset.awiRecentRemove);
      return;
    }

    const addButton = event.target.closest('[data-awi-search-add]');
    if (addButton) {
      event.preventDefault();
      this.addToCart(addButton);
      return;
    }

    // Any result link counts as a successful search — remember the term
    if (event.target.closest('[data-awi-search-result]') && this.searchTerm) {
      this.saveRecentSearch(this.searchTerm);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  onInput() {
    const value = this.input.value.trim();
    this.clearButton.hidden = value.length === 0;

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(value), AwiSearchModal.DEBOUNCE);
  }

  onClear() {
    this.input.value = '';
    this.clearButton.hidden = true;
    this.search('', { syncInput: true });
    this.input.focus();
  }

  onSubmit(event) {
    const value = this.input.value.trim();

    if (!value.length) {
      event.preventDefault();
      return;
    }

    this.saveRecentSearch(value);
  }

  /* ------------------------------------------------------------ open/close */

  open(opener) {
    if (this.hasAttribute('open')) return;

    this.openerElement = opener || document.activeElement;
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    this.renderRecentSearches();

    // Wait a frame so the element is painted before focusing / measuring
    requestAnimationFrame(() => {
      this.input.focus();
      this.input.select();
      this.updateCarouselArrows?.();
    });
  }

  close() {
    if (!this.hasAttribute('open')) return;

    clearTimeout(this.debounceTimer);
    this.abortController?.abort();
    this.abortController = null;

    this.removeAttribute('open');
    this.removeAttribute('loading');
    document.body.classList.remove('overflow-hidden');

    if (this.openerElement && document.body.contains(this.openerElement)) {
      this.openerElement.focus();
    }
    this.openerElement = null;
  }

  trapFocus(event) {
    const focusable = Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------- search */

  // `syncInput` writes the term back into the field, which is what a click on a
  // suggestion or recent search should do. It must stay off for typing: the term
  // is trimmed before searching, so echoing it back would delete a space the
  // moment the debounce fires, mid-word.
  search(term, { focus = false, syncInput = false } = {}) {
    const value = (term || '').trim();

    if (syncInput && this.input.value !== value) this.input.value = value;
    this.clearButton.hidden = this.input.value.length === 0;
    if (focus) this.input.focus();

    this.searchTerm = value;

    if (value.length < 2) {
      this.showIdleState();
      return;
    }

    this.renderResults(value);
  }

  async renderResults(term) {
    const key = term.toLowerCase();

    if (this.cache.has(key)) {
      this.injectResults(this.cache.get(key));
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setAttribute('loading', '');

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,query,article,page',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      section_id: 'awi-predictive-search',
    });

    try {
      const response = await fetch(`${this.predictiveSearchUrl}?${params}`, {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const text = await response.text();
      const markup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-awi-predictive-search')?.innerHTML;

      if (markup === undefined) throw new Error('Predictive search section missing from response');

      this.cache.set(key, markup);

      // A newer keystroke may have landed while this request was in flight
      if (this.searchTerm.toLowerCase() !== key) return;

      this.injectResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.removeAttribute('loading');
      console.error(error);
    }
  }

  injectResults(markup) {
    this.removeAttribute('loading');
    this.resultsState.innerHTML = markup;
    this.resultsState.hidden = false;
    this.idleState.hidden = true;
    this.input.setAttribute('aria-expanded', 'true');
    this.scroller.scrollTop = 0;
  }

  showIdleState() {
    this.removeAttribute('loading');
    this.resultsState.hidden = true;
    this.resultsState.innerHTML = '';
    this.idleState.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
  }

  /* -------------------------------------------------------- recent searches */

  readRecentSearches() {
    try {
      const stored = JSON.parse(localStorage.getItem(AwiSearchModal.STORAGE_KEY));
      return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  writeRecentSearches(terms) {
    try {
      localStorage.setItem(AwiSearchModal.STORAGE_KEY, JSON.stringify(terms));
    } catch (error) {
      // Storage unavailable (private browsing / quota) — recent searches are optional
    }
  }

  saveRecentSearch(term) {
    const value = term.trim();
    if (!value) return;

    const terms = this.readRecentSearches().filter((existing) => existing.toLowerCase() !== value.toLowerCase());
    terms.unshift(value);
    this.writeRecentSearches(terms.slice(0, AwiSearchModal.MAX_RECENT));
  }

  removeRecentSearch(term) {
    this.writeRecentSearches(this.readRecentSearches().filter((existing) => existing !== term));
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    const terms = this.readRecentSearches();

    this.recent.hidden = terms.length === 0;
    this.recentList.innerHTML = '';

    terms.forEach((term) => {
      const item = document.createElement('li');
      item.className = 'awi-search__chip';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'awi-search__chip-term';
      button.textContent = term;
      button.setAttribute('data-awi-search-term', term);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'awi-search__chip-remove';
      remove.setAttribute('data-awi-recent-remove', term);
      remove.setAttribute('aria-label', `Remove ${term}`);
      remove.innerHTML = '<svg class="icon" viewBox="0 0 18 17" aria-hidden="true" focusable="false"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';

      item.append(button, remove);
      this.recentList.append(item);
    });
  }

  /* -------------------------------------------------------------- quick add */

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const body = {
      items: [{ id: Number(variantId), quantity: 1 }],
    };

    if (cartDrawer) {
      body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    button.disabled = true;

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Add to cart failed');

      if (this.searchTerm) this.saveRecentSearch(this.searchTerm);

      if (cartDrawer && result.sections) {
        this.close();
        cartDrawer.renderContents(result);
      } else {
        await this.refreshCartIcon();
        button.classList.add('awi-search-card__add--added');
        setTimeout(() => button.classList.remove('awi-search-card__add--added'), 1500);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
    }
  }

  async refreshCartIcon() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
      const text = await response.text();
      const markup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('.shopify-section')?.innerHTML;

      if (markup) bubble.innerHTML = markup;
    } catch (error) {
      console.error(error);
    }
  }

  /* --------------------------------------------------------------- carousel */

  setupCarousel() {
    const carousel = this.querySelector('[data-awi-carousel]');
    if (!carousel) return;

    const track = carousel.querySelector('[data-awi-carousel-track]');
    const prev = carousel.querySelector('[data-awi-carousel-prev]');
    const next = carousel.querySelector('[data-awi-carousel-next]');
    if (!track) return;

    const step = () => track.clientWidth * 0.8;

    const updateArrows = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 1;
      prev.disabled = track.scrollLeft <= 0;
      next.disabled = track.scrollLeft >= maxScroll;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);

    this.updateCarouselArrows = updateArrows;
    updateArrows();
  }
}

customElements.define('awi-search-modal', AwiSearchModal);

/* Open the modal from any trigger in the page (header icon, mobile drawer, …) */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-awi-search-open]');
  if (!trigger) return;

  const modal = document.getElementById('AwiSearchModal');
  if (!modal) return;

  event.preventDefault();
  modal.open(trigger);
});
